import React, { useState, useEffect, useRef } from 'react';
import { Send, Stethoscope, Loader2, MessageCircle, Reply, ChevronDown, X, Paperclip, Image as ImageIcon, File, Download, Eye, ChevronLeft, ChevronRight, Mic, Volume2 } from 'lucide-react';
import VoiceRecorder from './VoiceRecorder';
import ChatAudioPlayer from './ChatAudioPlayer';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import baseURL from '../../utils/baseURL';
import EmojiPicker from './EmojiPicker';
import MessageReactionPicker from './MessageReactionPicker';
import MessageStatus from './MessageStatus';
import { formatDateHeader } from '../../utils/dateHeader';
import chatBackground from '../../assets/img/theraputimechatbackground.jpg';

import getFileURL from '../../utils/fileURL';


const DoctorChatInterface = ({ socket, doctor, patientId }) => {
    const { t } = useTranslation(['chat']);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [inputError, setInputError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const historyLoadedRef = useRef(false);
    const currentChatRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const chatAreaRef = useRef(null);
    const [roomId, setRoomId] = useState(null);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [showScrollBottom, setShowScrollBottom] = useState(false);

    // File upload state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [previewImages, setPreviewImages] = useState([]);
    const [previewImageIndex, setPreviewImageIndex] = useState(0);
    const fileInputRef = useRef(null);

    const handleScroll = () => {
        if (!chatAreaRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollBottom(!isNearBottom);
    };

    const scrollToMessage = (msgId) => {
        if (!msgId) return;
        const element = document.getElementById(`message-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50', 'transition-all');
            setTimeout(() => {
                element.classList.remove('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50', 'transition-all');
            }, 2000);
        }
    };

    // Maximum length for message input
    const MAX_MESSAGE_LENGTH = 1000;

    // Load chat history when doctor changes
    useEffect(() => {
        if (!doctor || !doctor.doctorId || !socket) return;

        // Reset state only when actual chat target changes
        const chatKey = `${doctor.doctorId}-${patientId}`;
        if (currentChatRef.current !== chatKey) {
            setMessages([]);
            currentChatRef.current = chatKey;
        }
        setRoomId(null);
        setIsLoading(true);
        setIsTyping(false);
        setIsOnline(false);
        historyLoadedRef.current = false;

        // Initialize chat
        socket.emit('initChat', {
            doctorId: doctor.doctorId,
            patientId,
        });

        const handleChatInitialized = (data) => {
            console.log("chatInitialized:", data);
            const newRoomId = data.roomId;
            setRoomId(newRoomId);

            // Join Room
            socket.emit('joinRoom', {
                roomId: newRoomId,
                userId: patientId,
                role: "patient"
            });

            // Fetch History
            socket.emit('fetchHistory', {
                roomId: newRoomId,
                page: 1,
                limit: 50,
            });
        };

        const handleRoomSummary = (summary) => {
            console.log("roomSummary:", summary);
            if (summary.roomId) setRoomId(summary.roomId);

            // Update online status from room summary
            if (summary.doctor) {
                setIsOnline(summary.doctor.isOnline || false);
            }
        };

        const handleHistory = (data) => {
            console.log("history received:", data);
            historyLoadedRef.current = true;
            const formattedMessages = (data.messages || []).map(msg => ({
                ...msg,
                id: msg.messageId || msg._id || msg.id,
                timestamp: msg.createdAt ? new Date(msg.createdAt) : null,
                type: msg.senderRole === 'patient' ? 'user' : 'doctor',
                content: msg.message,
                reactions: msg.reactions || {},
                isRead: msg.isRead || false,
                replyTo: msg.replyTo || null,
                attachments: msg.attachments || []
            }));
            setMessages(prev => {
                const merged = [...formattedMessages];
                // Add any messages from prev that aren't in history (e.g., optimistic/pending messages)
                prev.forEach(m => {
                    const id = m.id || m._id;
                    if (!formattedMessages.find(fm => (fm.id || fm._id) === id)) {
                        merged.push(m);
                    }
                });
                return merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            });
            setIsLoading(false);
        };

        const handleReceiveMessage = (msg) => {
            console.log("receiveMessage:", msg);
            setMessages(prev => {
                const formattedMsg = {
                    ...msg,
                    id: msg.messageId || msg._id || msg.id,
                    timestamp: msg.createdAt ? new Date(msg.createdAt) : null,
                    type: msg.senderRole === 'patient' ? 'user' : 'doctor',
                    content: msg.message,
                    reactions: msg.reactions || {},
                    isRead: msg.isRead || false,
                    replyTo: msg.replyTo || null,
                    attachments: msg.attachments || []
                };

                // Reconcile echo of our own optimistic message with server timestamp/id
                if (msg.senderId === patientId && msg.senderRole === "patient" && (!msg.attachments || msg.attachments.length === 0)) {
                    const optimisticIndex = prev.findIndex(m =>
                        !m._id && !m.messageId &&
                        m.type === 'user' &&
                        m.content === msg.message
                    );
                    if (optimisticIndex !== -1) {
                        const updated = [...prev];
                        updated[optimisticIndex] = { ...updated[optimisticIndex], ...formattedMsg };
                        return updated;
                    }
                    return prev;
                }

                return [...prev, formattedMsg];
            });
        };

        const handleUserTyping = (data) => {
            console.log("userTyping:", data);
            // Only show typing if it's the doctor typing (not me)
            if (data.role === 'doctor' && data.userId === doctor.doctorId) {
                setIsTyping(true);
            }
        };

        const handleUserStoppedTyping = (data) => {
            console.log("userStoppedTyping:", data);
            if (data.role === 'doctor' && data.userId === doctor.doctorId) {
                setIsTyping(false);
            }
        };

        const handleUserStatusUpdate = (data) => {
            console.log("userStatusUpdate:", data);
            // Update online status if it's the doctor
            if (data.role === 'doctor' && data.userId === doctor.doctorId) {
                setIsOnline(data.isOnline || false);
            }
        };

        const handleMessagesSeen = (data) => {
            console.log("messagesSeen:", data);
            // Update message status to read when doctor sees them
            if (data.role === 'doctor') {
                setMessages(prev => prev.map(msg =>
                    msg.type === 'user' ? { ...msg, isRead: true, status: 'read' } : msg
                ));
            }
        };

        const handleReconnect = () => {
            // Re-initialize chat on reconnect to restore room and fetch history
            socket.emit('initChat', {
                doctorId: doctor.doctorId,
                patientId,
            });
        };

        socket.on('chatInitialized', handleChatInitialized);
        socket.on('roomSummary', handleRoomSummary);
        socket.on('history', handleHistory);
        socket.on('receiveMessage', handleReceiveMessage);
        socket.on('userTyping', handleUserTyping);
        socket.on('userStoppedTyping', handleUserStoppedTyping);
        socket.on('userStatusUpdate', handleUserStatusUpdate);
        socket.on('messagesSeen', handleMessagesSeen);
        socket.on('connect', handleReconnect);

        return () => {
            socket.off('chatInitialized', handleChatInitialized);
            socket.off('roomSummary', handleRoomSummary);
            socket.off('history', handleHistory);
            socket.off('receiveMessage', handleReceiveMessage);
            socket.off('userTyping', handleUserTyping);
            socket.off('userStoppedTyping', handleUserStoppedTyping);
            socket.off('userStatusUpdate', handleUserStatusUpdate);
            socket.off('messagesSeen', handleMessagesSeen);
            socket.off('connect', handleReconnect);
        }

    }, [doctor?.doctorId, patientId, socket]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Mark messages as seen when viewing the chat
    useEffect(() => {
        if (!roomId || !socket || messages.length === 0) return;

        // Mark as seen after a short delay to ensure user is actually viewing
        const timer = setTimeout(() => {
            socket.emit('markAsSeen', {
                roomId,
                userId: patientId,
                role: 'patient'
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [roomId, socket, patientId, messages.length]);

    const handleEmojiSelect = (emoji) => {
        setInput(prev => prev + emoji);
        inputRef.current?.focus();
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + selectedFiles.length > 5) {
            alert(t('chat.max_files_error'));
            return;
        }

        const validFiles = files.filter(file => {
            const isLt10M = file.size / 1024 / 1024 < 10;
            return isLt10M;
        });

        setSelectedFiles(prev => [...prev, ...validFiles]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDownload = async (fileUrl, fileName) => {
        if (!fileUrl) return;
        try {
            const filename = fileUrl.split('/').pop();
            const downloadUrl = `${baseURL}/chat/download/${filename}`;
            console.log("Downloading from:", downloadUrl);

            const response = await fetch(downloadUrl);
            if (!response.ok) throw new Error('Network response was not ok');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading file:', error);
            // Fallback
            const filename = fileUrl.split('/').pop();
            window.open(`${baseURL}/chat/download/${filename}`, '_blank');
        }
    };

    const validateMessageInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > MAX_MESSAGE_LENGTH) {
            setInputError(t('chat.message_too_long', { length: MAX_MESSAGE_LENGTH }));
            return false;
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            setInputError(t('chat.message_spaces_only'));
            return false;
        }

        setInputError('');
        return true;
    };

    // Handle typing indicator
    const handleInputChange = (e) => {
        const value = e.target.value;

        // Clear error if user is typing
        if (inputError && value.length < input.length) {
            setInputError('');
        }

        // Validate input
        if (validateMessageInput(value)) {
            // Sanitize input - remove dangerous characters
            const sanitized = value.replace(/[<>]/g, '');
            setInput(sanitized);

            if (!roomId || !socket) return;

            // Emit typing event
            if (sanitized.trim()) {
                socket.emit('typing', {
                    roomId,
                    userId: patientId,
                    role: 'patient'
                });

                // Clear existing timeout
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }

                // Stop typing after 2 seconds of no input
                typingTimeoutRef.current = setTimeout(() => {
                    socket.emit('stopTyping', {
                        roomId,
                        userId: patientId,
                        role: 'patient'
                    });
                }, 2000);
            } else {
                // If input is empty, stop typing immediately
                socket.emit('stopTyping', {
                    roomId,
                    userId: patientId,
                    role: 'patient'
                });
            }
        }
    };

    const handleMessageReaction = (messageId, emoji) => {
        setMessages(prev => prev.map(msg => {
            if (msg.id === messageId) {
                const newReactions = { ...msg.reactions };
                if (newReactions[emoji]) {
                    // Remove reaction if already exists
                    delete newReactions[emoji];
                } else {
                    // Add reaction
                    newReactions[emoji] = (newReactions[emoji] || 0) + 1;
                }
                return { ...msg, reactions: newReactions };
            }
            return msg;
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((!input.trim() && selectedFiles.length === 0) || isLoading || isUploading) return;

        if (input.trim() && !validateMessageInput(input)) {
            return;
        }

        const userMessage = input.trim();
        const currentFiles = [...selectedFiles];
        const currentRoomId = roomId || `room_${doctor.doctorId}_${patientId}`;
        const currentReplyToId = replyToMessage ? (replyToMessage.id || replyToMessage._id) : null;

        if (currentFiles.length > 0) {
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('roomId', currentRoomId);
                formData.append('senderId', patientId);
                formData.append('senderRole', 'patient');
                if (userMessage) formData.append('message', userMessage);
                formData.append('doctorId', doctor.doctorId);
                formData.append('patientId', patientId);
                if (currentReplyToId) formData.append('replyTo', currentReplyToId);

                currentFiles.forEach(file => {
                    formData.append('files', file);
                });

                await axios.post(`${baseURL}/chat/send`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                setInput('');
                setSelectedFiles([]);
                setInputError('');
                setReplyToMessage(null);
            } catch (error) {
                console.error("Error sending message with files:", error);
            } finally {
                setIsUploading(false);
            }
        } else {
            setInput('');
            setInputError('');

            const newUserMessage = {
                id: Date.now(),
                type: 'user',
                content: userMessage,
                timestamp: new Date(),
                reactions: {},
                status: 'sent',
                replyTo: replyToMessage ? { ...replyToMessage, id: replyToMessage._id || replyToMessage.id } : null,
                attachments: []
            };
            setMessages(prev => [...prev, newUserMessage]);
            setReplyToMessage(null);

            socket.emit('sendMessage', {
                roomId: currentRoomId,
                senderId: patientId,
                senderRole: "patient",
                message: userMessage,
                doctorId: doctor.doctorId,
                patientId: patientId,
                replyTo: currentReplyToId
            });
        }
    };

    const formatTime = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return t('doctorChat.invalidDate');
        return d.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,        // <-- This makes AM/PM appear
        });
    };


    return (
        <div className="flex flex-col h-full"
            style={{
                backgroundImage: `url(${chatBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed'
            }}
            data-testid="doctor-chat-container"
        >
            {/* Chat Header */}
            <div className="bg-white p-3 flex items-center gap-3 shrink-0 shadow-sm" data-testid="doctor-chat-header">
                <div className="relative" data-testid="doctor-chat-avatar-container">
                    {(doctor?.avatar || doctor?.image) ? (
                        <img
                            src={getFileURL(doctor?.avatar || doctor?.image)}
                            alt={doctor?.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-[#4ABDBD] shadow-md"
                            data-testid="doctor-chat-avatar-image"
                        />
                    ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#4ebfbf] to-[#3A9B9B] flex items-center justify-center border-2 border-white shadow-md" data-testid="doctor-chat-avatar-placeholder">
                            <span className="text-white text-xl font-semibold" data-testid="doctor-chat-avatar-initial">
                                {doctor?.name?.charAt(0)?.toUpperCase()}
                            </span>
                        </div>
                    )
                    }
                    {isOnline && (
                        <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full" data-testid="doctor-chat-online-indicator"></span>
                    )}
                </div>
                <div className="flex-1" data-testid="doctor-chat-header-info">
                    <h3 className="font-semibold text-gray-800 text-lg" data-testid="doctor-chat-doctor-name">{doctor?.name || t('chat.doctor')}</h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1" data-testid="doctor-chat-status-text">
                        {isTyping ? (
                            <span className="text-[#4ABDBD] font-medium" data-testid="doctor-chat-header-typing-text">{t('chat.typing')}</span>
                        ) : isOnline ? (
                            <>
                                <span className="w-2 h-2 bg-green-500 rounded-full" data-testid="doctor-chat-online-dot"></span>
                                {t('chat.online')}
                            </>
                        ) : (
                            <>
                                <span className="w-2 h-2 bg-gray-400 rounded-full" data-testid="doctor-chat-offline-dot"></span>
                                {t('chat.offline')}
                            </>
                        )}
                    </p>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 relative min-h-0 flex flex-col" data-testid="doctor-chat-messages-section">
                <div
                    ref={chatAreaRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar"
                    data-testid="doctor-chat-messages-area"
                >
                    {messages.length === 0 && (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-2" data-testid="doctor-chat-empty-state">
                            <MessageCircle size={48} className="opacity-20" data-testid="doctor-chat-empty-icon" />
                            <p data-testid="doctor-chat-empty-text">{t('chat.startConversation', { name: doctor?.name })}</p>
                        </div>
                    )}

                    {messages.length > 0 && messages.map((msg, index) => {
                        const currentDate = formatDateHeader(msg.timestamp);
                        const previousDate = index > 0 ? formatDateHeader(messages[index - 1].timestamp) : null;
                        const showHeader = currentDate !== previousDate;

                        return (
                            <React.Fragment key={msg._id || msg.id}>
                                {showHeader && (
                                    <div className="flex justify-center my-4" data-testid={`doctor-chat-date-header-${msg._id || msg.id}`}>
                                        <span className="bg-gray-200 text-gray-700 px-4 py-1 rounded-full text-xs shadow-sm" data-testid={`doctor-chat-date-text-${msg._id || msg.id}`}>
                                            {currentDate}
                                        </span>
                                    </div>
                                )}

                                <div
                                    id={`message-${msg._id || msg.id}`}
                                    key={msg._id || msg.id}
                                    className={`flex flex-col ${msg.type === 'user' ? 'items-end' : 'items-start'} group/msg py-1.5`}
                                    data-testid={`doctor-chat-message-${msg._id || msg.id}`}
                                >
                                    <div className="flex items-end gap-2 max-w-[75%] relative" data-testid={`doctor-chat-message-row-${msg._id || msg.id}`}>
                                        <div
                                            className={`rounded-2xl p-4 shadow-md relative transition-all duration-300 ${msg.type === 'user'
                                                ? 'bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white rounded-tr-none'
                                                : 'bg-white text-gray-800 border border-gray-200 rounded-tl-none shadow-sm'
                                                }`}
                                            data-testid={`doctor-chat-message-bubble-${msg._id || msg.id}`}
                                        >
                                            {msg.replyTo && (typeof msg.replyTo === 'object' || msg.replyTo) && (
                                                <div
                                                    onClick={() => scrollToMessage(msg.replyTo.id || msg.replyTo._id || msg.replyTo)}
                                                    className={`mb-2 p-2 rounded-xl text-xs border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${msg.type === 'user' ? 'bg-white/10 border-white/40 text-white/90' : 'bg-gray-50 border-[#4ABDBD] text-gray-600'
                                                        }`}
                                                    data-testid={`doctor-chat-reply-quote-${msg._id || msg.id}`}>
                                                    <div className="font-semibold text-[10px] mb-0.5 opacity-80 flex items-center gap-1" data-testid={`doctor-chat-reply-sender-${msg._id || msg.id}`}>
                                                        <Reply size={10} data-testid={`doctor-chat-reply-icon-${msg._id || msg.id}`} />
                                                        {msg.replyTo.senderRole === 'doctor' ? t('chat.doctor') : t('chat.patient')}
                                                    </div>
                                                    <div className="line-clamp-2 truncate" data-testid={`doctor-chat-reply-text-${msg._id || msg.id}`}>{msg.replyTo.message || msg.replyTo.content || t('chat.attachment')}</div>
                                                </div>
                                            )}
                                            <div className="whitespace-pre-wrap leading-relaxed text-sm" data-testid={`doctor-chat-message-content-${msg._id || msg.id}`}>
                                                {msg.content}
                                            </div>

                                            {/* Attachments */}
                                            {msg.attachments && msg.attachments.length > 0 && (
                                                <div className="mt-3 min-w-[200px]" data-testid={`doctor-chat-attachments-${msg._id || msg.id}`}>
                                                    {(() => {
                                                        const images = msg.attachments.filter(f => f.fileType === 'image' || (f.mimeType && f.mimeType.startsWith('image/')));
                                                        const otherFiles = msg.attachments.filter(f => !(f.fileType === 'image' || (f.mimeType && f.mimeType.startsWith('image/'))));

                                                        return (
                                                            <div className="grid gap-2">
                                                                {/* Image Gallery */}
                                                                {images.length > 0 && (
                                                                    <div
                                                                        className={`relative rounded-xl overflow-hidden border ${msg.type === 'user' ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'} cursor-pointer group`}
                                                                        onClick={() => {
                                                                            setPreviewImages(images);
                                                                            setPreviewImageIndex(0);
                                                                        }}
                                                                        data-testid={`doctor-chat-image-gallery-${msg._id || msg.id}`}
                                                                    >
                                                                        <img
                                                                            src={getFileURL(images[0].url)}
                                                                            alt={images[0].fileName || t('chat.image_alt', 'Image')}
                                                                            className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                                                            data-testid={`doctor-chat-image-${msg._id || msg.id}`}
                                                                        />
                                                                        {images.length > 1 && (
                                                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center" data-testid={`doctor-chat-image-overlay-${msg._id || msg.id}`}>
                                                                                <span className="text-white text-3xl font-bold" data-testid={`doctor-chat-image-count-${msg._id || msg.id}`}>+{images.length - 1}</span>
                                                                            </div>
                                                                        )}
                                                                        {images.length === 1 && (
                                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 z-10">
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleDownload(images[0].url, images[0].fileName);
                                                                                    }}
                                                                                    className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100"
                                                                                    data-testid={`doctor-chat-image-download-button-${msg._id || msg.id}`}
                                                                                >
                                                                                    <Download size={20} data-testid={`doctor-chat-image-download-icon-${msg._id || msg.id}`} />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Other Files */}
                                                                {otherFiles.map((file, i) => (
                                                                    <div key={`file-${i}`} className={`rounded-xl overflow-hidden border ${msg.type === 'user' ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`} data-testid={`doctor-chat-file-attachment-${msg._id || msg.id}-${i}`}>
                                                                        {file.fileType === 'voice' || (file.mimeType && file.mimeType.startsWith('audio/')) ? (
                                                                            <ChatAudioPlayer src={getFileURL(file.url)} isUser={msg.type === 'user'} />
                                                                        ) : (
                                                                            <div className="p-3 flex items-center gap-3" data-testid={`doctor-chat-file-row-${msg._id || msg.id}-${i}`}>
                                                                                <div className={`p-2 rounded-lg ${msg.type === 'user' ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                                                    <File size={20} data-testid={`doctor-chat-file-icon-${msg._id || msg.id}-${i}`} />
                                                                                </div>
                                                                                <div className="flex-1 min-w-0">
                                                                                    <p className="text-xs font-bold truncate" data-testid={`doctor-chat-file-name-${msg._id || msg.id}-${i}`}>{file.fileName}</p>
                                                                                    <p className="text-[10px] opacity-60 uppercase" data-testid={`doctor-chat-file-type-${msg._id || msg.id}-${i}`}>{file.mimeType ? file.mimeType.split('/')[1] : t('doctorChat.file')}</p>
                                                                                </div>
                                                                                <button
                                                                                    onClick={() => handleDownload(file.url, file.fileName)}
                                                                                    className={`p-2 rounded-lg transition-colors ${msg.type === 'user' ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                                                    data-testid={`doctor-chat-file-download-button-${msg._id || msg.id}-${i}`}
                                                                                >
                                                                                    <Download size={18} data-testid={`doctor-chat-file-download-icon-${msg._id || msg.id}-${i}`} />
                                                                                </button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between gap-2 mt-2" data-testid={`doctor-chat-message-meta-${msg._id || msg.id}`}>
                                                <div className={`flex items-center text-[10px] ${msg.type === 'user' ? 'text-white/70' : 'text-gray-400'
                                                    }`} data-testid={`doctor-chat-message-timestamp-${msg._id || msg.id}`}>
                                                    {formatTime(msg.timestamp)}
                                                    {msg.type === 'user' && msg.status && (
                                                        <MessageStatus status={msg.status} />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {/* {msg.type === 'user' && (
                                <div className="flex-shrink-0">
                                    <MessageReactionPicker
                                        onReactionSelect={(emoji) => handleMessageReaction(msg.id, emoji)}
                                        position="top"
                                        align="right"
                                    />
                                </div>
                            )} */}
                                        {/* Reply Button (Hover) */}
                                        <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-1 z-10 ${msg.type === 'user' ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]'}`} data-testid={`doctor-chat-reply-action-${msg._id || msg.id}`}>
                                            <button
                                                onClick={() => setReplyToMessage(msg)}
                                                className="p-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-full shadow-sm transition-all"
                                                title={t('doctorChat.reply')}
                                                data-testid={`doctor-chat-reply-button-${msg._id || msg.id}`}
                                            >
                                                <Reply size={14} data-testid={`doctor-chat-reply-action-icon-${msg._id || msg.id}`} />
                                            </button>
                                        </div>
                                    </div>
                                    {/* Reactions */}
                                    {Object.keys(msg.reactions || {}).length > 0 && (
                                        <div className={`flex gap-1 mt-1 ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-testid={`doctor-chat-reactions-${msg._id || msg.id}`}>
                                            {Object.entries(msg.reactions).map(([emoji, count]) => (
                                                <button
                                                    key={emoji}
                                                    type="button"
                                                    onClick={() => handleMessageReaction(msg.id, emoji)}
                                                    className="bg-white border border-gray-200 rounded-full px-2 py-0.5 text-xs flex items-center gap-1 shadow-sm hover:shadow-md transition-shadow"
                                                    data-testid={`doctor-chat-reaction-button-${msg._id || msg.id}-${emoji}`}
                                                >
                                                    <span data-testid={`doctor-chat-reaction-emoji-${msg._id || msg.id}-${emoji}`}>{emoji}</span>
                                                    <span className="text-gray-600 font-medium" data-testid={`doctor-chat-reaction-count-${msg._id || msg.id}-${emoji}`}>{count}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        )
                    })
                    }
                    {isLoading && (
                        <div className="flex justify-start" data-testid="doctor-chat-loading-container">
                            <div className="bg-white text-gray-800 border border-gray-200 rounded-2xl rounded-tl-none p-4 shadow-sm">
                                <Loader2 className="animate-spin text-[#4ABDBD]" size={20} data-testid="doctor-chat-loading-icon" />
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
                <AnimatePresence>
                    {showScrollBottom && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.8, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.8, y: 10 }}
                            onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                            className="absolute bottom-4 right-6 p-3 bg-[#4ABDBD] text-white rounded-full shadow-lg hover:bg-[#3A9B9B] transition-all z-10 flex items-center justify-center group"
                            title={t('chat.recent_messages')}
                            data-testid="doctor-chat-scroll-bottom-button"
                        >
                            <ChevronDown size={20} className="group-hover:translate-y-0.5 transition-transform" data-testid="doctor-chat-scroll-bottom-icon" />
                        </motion.button>
                    )}
                </AnimatePresence>
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-200" data-testid="doctor-chat-input-area">
                {replyToMessage && (
                    <div className="mb-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 flex items-start justify-between shadow-sm animate-in slide-in-from-bottom-2" data-testid="doctor-chat-reply-preview-container">
                        <div className="flex-1 min-w-0 pr-4">
                            <div className="flex items-center gap-1 text-xs font-semibold text-[#4ABDBD] mb-1" data-testid="doctor-chat-reply-preview-label">
                                <Reply size={12} data-testid="doctor-chat-reply-preview-icon" />
                                {t('doctorChat.replyingTo')} {replyToMessage.type === 'user' ? t('chat.patient') : t('chat.doctor')}
                            </div>
                            <p className="text-sm text-gray-600 truncate" data-testid="doctor-chat-reply-preview-content">{replyToMessage.content || t('chat.attachment', 'Attachment')}</p>
                        </div>
                        <button
                            onClick={() => setReplyToMessage(null)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                            data-testid="doctor-chat-reply-preview-close-button"
                        >
                            <X size={16} data-testid="doctor-chat-reply-preview-close-icon" />
                        </button>
                    </div>
                )}
                {/* Typing Indicator - Teams Style */}
                {isTyping && (
                    <div className="mb-2 px-2 py-1.5 flex items-center gap-2 text-sm text-gray-600 animate-fade-in" data-testid="doctor-chat-typing-indicator">
                        <div className="flex gap-1">
                            <span className="w-2 h-2 bg-[#4ABDBD] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-2 h-2 bg-[#4ABDBD] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                            <span className="w-2 h-2 bg-[#4ABDBD] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                        <span className="font-medium" data-testid="doctor-chat-typing-name">{doctor?.name}</span>
                        <span className="text-gray-500" data-testid="doctor-chat-typing-text">{t('chat.isTyping')}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="flex flex-col gap-2" data-testid="doctor-chat-form">
                    {/* File Previews */}
                    {selectedFiles.length > 0 && (
                        <div className="mb-1 flex flex-wrap gap-2 animate-in slide-in-from-bottom-2" data-testid="doctor-chat-file-previews-container">
                            {selectedFiles.map((file, index) => (
                                <div key={index} className="relative group bg-gray-50 border border-gray-200 rounded-xl p-2 pr-9 flex items-center gap-2 shadow-sm" data-testid={`doctor-chat-file-preview-${index}`}>
                                    {file.type.startsWith('image/') ? (
                                        <div className="w-8 h-8 rounded-lg bg-[#4ABDBD]/10 flex items-center justify-center overflow-hidden">
                                            <ImageIcon size={16} className="text-[#4ABDBD]" data-testid={`doctor-chat-file-preview-image-icon-${index}`} />
                                        </div>
                                    ) : (
                                        <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                                            <File size={16} className="text-gray-500" data-testid={`doctor-chat-file-preview-file-icon-${index}`} />
                                        </div>
                                    )}
                                    <span className="text-[11px] font-bold text-gray-700 truncate max-w-[100px]" data-testid={`doctor-chat-file-preview-name-${index}`}>{file.name}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeFile(index)}
                                        className="absolute right-1 text-gray-400 hover:text-red-500 p-1 transition-colors"
                                        data-testid={`doctor-chat-file-preview-remove-button-${index}`}
                                    >
                                        <X size={14} data-testid={`doctor-chat-file-preview-remove-icon-${index}`} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex gap-2 items-center" data-testid="doctor-chat-input-row">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            multiple
                            className="hidden"
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                            data-testid="doctor-chat-file-input"
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="p-2 text-gray-400 hover:text-[#4ABDBD] hover:bg-[#4ABDBD]/5 rounded-xl transition-all border border-gray-300 bg-gray-50 hover:bg-white"
                            title={t('chat.attach_files')}
                            data-testid="doctor-chat-attach-button"
                        >
                            <Paperclip size={20} data-testid="doctor-chat-attach-icon" />
                        </button>

                        <div className="flex-1 relative" data-testid="doctor-chat-input-wrapper">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={handleInputChange}
                                placeholder={t('chat.typeMessage')}
                                maxLength={MAX_MESSAGE_LENGTH}
                                className={`w-full h-12 px-4 pr-12 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all bg-gray-50 focus:bg-white ${inputError
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-300'
                                    }`}
                                disabled={isLoading || isUploading}
                                data-testid="doctor-chat-message-input"
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="doctor-chat-emoji-picker-wrapper">
                                <EmojiPicker onEmojiSelect={handleEmojiSelect} position="top" />
                            </div>
                            {inputError && (
                                <p className="text-red-500 text-xs mt-1 absolute -bottom-5 left-0" data-testid="doctor-chat-input-error">{inputError}</p>
                            )}
                        </div>
                        {input.trim() || selectedFiles.length > 0 ? (
                            <button
                                type="submit"
                                disabled={!!inputError || (!input.trim() && selectedFiles.length === 0) || isLoading || isUploading}
                                className={`h-12 w-12 rounded-full flex items-center justify-center transition-all ${!!inputError || (!input.trim() && selectedFiles.length === 0) || isLoading || isUploading
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white hover:shadow-lg shadow-md'
                                    }`}
                                data-testid="doctor-chat-send-button"
                            >
                                {isLoading || isUploading ? (
                                    <Loader2 size={20} className="animate-spin" data-testid="doctor-chat-send-loading-icon" />
                                ) : (
                                    <Send size={20} data-testid="doctor-chat-send-icon" />
                                )}
                            </button>
                        ) : (
                            <VoiceRecorder
                                isUploading={isUploading}
                                disabled={isLoading}
                                onRecordingComplete={(file) => {
                                    const formData = new FormData();
                                    const currentRoomId = roomId || `room_${doctor.doctorId}_${patientId}`;
                                    // In DoctorChatInterface, the user is the patient
                                    formData.append('roomId', currentRoomId);
                                    formData.append('senderId', patientId);
                                    formData.append('senderRole', 'patient');
                                    formData.append('doctorId', doctor.doctorId);
                                    formData.append('patientId', patientId);
                                    formData.append('files', file);

                                    setIsUploading(true);
                                    axios.post(`${baseURL}/chat/send`, formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                    }).then(() => {
                                        // Handle success
                                    }).catch(err => {
                                        console.error("Error sending voice note:", err);
                                        toast.warning(t('chat.sendFailed'));
                                    }).finally(() => {
                                        setIsUploading(false);
                                    });
                                }}
                            />
                        )}
                    </div>
                </form>
            </div>

            {/* Image Preview Modal */}
            <AnimatePresence>
                {previewImages.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-8"
                        onClick={() => setPreviewImages([])}
                        data-testid="doctor-chat-image-preview-modal"
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="relative flex justify-center items-center max-w-[100vw] sm:max-w-[90vw] max-h-[90vh]"
                            onClick={e => e.stopPropagation()}
                            data-testid="doctor-chat-image-preview-content"
                        >
                            {/* Action Buttons */}
                            <div className="absolute -top-14 right-0 flex items-center gap-3 z-[101]" data-testid="doctor-chat-image-preview-actions">
                                <button
                                    onClick={() => handleDownload(previewImages[previewImageIndex].url, previewImages[previewImageIndex].fileName)}
                                    className="p-2 sm:p-2.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all"
                                    title={t('doctorChat.downloadImage')}
                                    data-testid="doctor-chat-image-preview-download-button"
                                >
                                    <Download size={20} data-testid="doctor-chat-image-preview-download-icon" />
                                </button>
                                <button
                                    onClick={() => setPreviewImages([])}
                                    className="p-2 sm:p-2.5 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all"
                                    title={t('doctorChat.closePreview')}
                                    data-testid="doctor-chat-image-preview-close-button"
                                >
                                    <X size={20} data-testid="doctor-chat-image-preview-close-icon" />
                                </button>
                            </div>

                            {/* Previous Button */}
                            {previewImages.length > 1 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPreviewImageIndex(prev => prev === 0 ? previewImages.length - 1 : prev - 1);
                                    }}
                                    className="absolute left-2 sm:-left-16 top-1/2 -translate-y-1/2 p-2 sm:p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-[101]"
                                    data-testid="doctor-chat-image-preview-prev-button"
                                >
                                    <ChevronLeft size={28} data-testid="doctor-chat-image-preview-prev-icon" />
                                </button>
                            )}

                            {/* Next Button */}
                            {previewImages.length > 1 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPreviewImageIndex(prev => prev === previewImages.length - 1 ? 0 : prev + 1);
                                    }}
                                    className="absolute right-2 sm:-right-16 top-1/2 -translate-y-1/2 p-2 sm:p-3 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-[101]"
                                    data-testid="doctor-chat-image-preview-next-button"
                                >
                                    <ChevronRight size={28} data-testid="doctor-chat-image-preview-next-icon" />
                                </button>
                            )}

                            {/* Image */}
                            <img
                                src={getFileURL(previewImages[previewImageIndex].url)}
                                alt={previewImages[previewImageIndex].fileName || t('chat.preview', 'Preview')}
                                className="w-auto h-auto max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                                data-testid="doctor-chat-image-preview"
                            />

                            {/* Counter */}
                            {previewImages.length > 1 && (
                                <div className="absolute -bottom-14 left-1/2 -translate-x-1/2 text-white/90 bg-white/10 px-4 py-1.5 rounded-full text-sm font-medium tracking-wide z-[101]" data-testid="doctor-chat-image-preview-counter">
                                    {previewImageIndex + 1} / {previewImages.length}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default DoctorChatInterface;
